import type { MutableRefObject } from 'react';

import { FieldChangeTrigger } from '../../enums/form';
import { useLocalization } from '../../providers/LocaleProvider';
import type {
  ErrorMessagesMap,
  FieldChangeTriggerHandler,
  FieldValue,
  FormData,
  GetValidationErrorKey,
  ValidatedFieldChangeHandler,
  ValidationMap,
} from '../../types/form';
import { getValidationState } from './form-validation';

type ValidationMapOptions<FieldName extends string> =
  | {
      validationMap: ValidationMap<FieldName>;
    }
  | {
      validationMapRef: MutableRefObject<ValidationMap<FieldName>>;
    };

type UseFieldChangeHandlerOptions<FieldName extends string> = {
  formData: FormData<FieldName>;
  onFieldChange: ValidatedFieldChangeHandler<FieldName>;
  errorMessagesMap: ErrorMessagesMap<FieldName>;
  getValidationErrorKey?: GetValidationErrorKey<FieldName>;
  partialValidationOnChange?: boolean;
} & ValidationMapOptions<FieldName>;

interface FieldChangeHandlerWithValidationOptions<FieldName extends string> {
  fieldName: FieldName;
  value: string;
  trigger: FieldChangeTrigger;
}

const getValidationMap = <FieldName extends string>(options: ValidationMapOptions<FieldName>) => {
  if ('validationMapRef' in options) {
    return options.validationMapRef.current;
  } else if ('validationMap' in options) {
    return options.validationMap;
  }

  return new Map() as ValidationMap<FieldName>;
};

const getValidator = <FieldName extends string>(
  fieldName: FieldName,
  validationMapOptions: ValidationMapOptions<FieldName>
) => getValidationMap(validationMapOptions).get(fieldName);

export const useFieldChangeHandler = <FieldName extends string>({
  onFieldChange,
  formData,
  errorMessagesMap,
  partialValidationOnChange,
  getValidationErrorKey,
  ...validationMapOptions
}: UseFieldChangeHandlerOptions<FieldName>): FieldChangeTriggerHandler<FieldName> => {
  const { translate } = useLocalization();

  const validate = (field: FieldName, value: FieldValue) =>
    getValidationState({
      field,
      value,
      formData,
      getValidationErrorKey,
      translate,
      validator: getValidator(field, validationMapOptions),
    });

  const handleFieldChangeWithFullValidation = ({
    fieldName,
    value,
    trigger,
  }: FieldChangeHandlerWithValidationOptions<FieldName>) => {
    const { valid, errors } = validate(fieldName, value);

    onFieldChange({
      fieldName,
      value,
      valid,
      errors,
      trigger,
    });
  };

  const handleFieldChangeWithPartialValidation = ({
    fieldName,
    value,
    trigger,
  }: FieldChangeHandlerWithValidationOptions<FieldName>) => {
    const previousValueErrors = errorMessagesMap.get(fieldName) || [];
    if (previousValueErrors.length > 0) {
      const { errors: currentValueErrors } = validate(fieldName, value);
      const previousValueErrorsLeft = previousValueErrors.filter(error =>
        currentValueErrors.includes(error)
      );
      const isValid = previousValueErrorsLeft.length === 0;

      return onFieldChange({
        fieldName,
        value,
        valid: isValid,
        errors: previousValueErrorsLeft,
        trigger,
      });
    }

    onFieldChange({
      fieldName,
      value,
      valid: true,
      errors: [],
      trigger,
    });
  };

  return ({ fieldName, value, trigger }) => {
    if (partialValidationOnChange && trigger === FieldChangeTrigger.Change) {
      handleFieldChangeWithPartialValidation({ fieldName, value, trigger });
    } else {
      handleFieldChangeWithFullValidation({ fieldName, value, trigger });
    }
  };
};
